window.VAE = window.VAE || {};

VAE.AxeIntegration = (function () {
  'use strict';

  let axeLoaded = false;
  let axeLoadPromise = null;

  // Load axe-core dynamically
  function loadAxeCore() {
    if (axeLoadPromise) {
      return axeLoadPromise;
    }

    axeLoadPromise = new Promise((resolve, reject) => {
      if (window.axe) {
        axeLoaded = true;
        resolve(window.axe);
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://unpkg.com/axe-core@4.10.2/axe.min.js';
      script.async = true;
      
      script.onload = () => {
        if (window.axe) {
          axeLoaded = true;
          console.log('VAE.AxeIntegration: axe-core loaded successfully');
          resolve(window.axe);
        } else {
          reject(new Error('axe-core failed to load properly'));
        }
      };
      
      script.onerror = () => {
        reject(new Error('Failed to load axe-core script'));
      };

      document.head.appendChild(script);
    });

    return axeLoadPromise;
  }

  // Wait for DOM to stabilize before running analysis
  async function waitForDOMStability(timeout = 100, checkInterval = 100) {
    // 🔥 DEAKTIVIERT für Testing - DOM Stability Checks verursachen Endlosschleife
    return new Promise((resolve) => {
      console.log('VAE.AxeIntegration: [TESTING] DOM stability checks DISABLED - proceeding immediately');
      resolve();
    });
    
    // ORIGINAL CODE (deaktiviert):
    /*
    return new Promise((resolve) => {
      let lastDOMState = document.documentElement.outerHTML.length;
      let lastWidgetElementCount = document.querySelectorAll('[data-vae-id]').length;
      let stableCount = 0;
      const requiredStableChecks = 3; // DOM must be stable for 3 consecutive checks
      
      console.log('VAE.AxeIntegration: Starting DOM stability check...', {
        initialDOMLength: lastDOMState,
        initialWidgetElements: lastWidgetElementCount
      });
      
      const checkStability = () => {
        const currentDOMState = document.documentElement.outerHTML.length;
        const currentWidgetElementCount = document.querySelectorAll('[data-vae-id]').length;
        
        if (currentDOMState === lastDOMState && currentWidgetElementCount === lastWidgetElementCount) {
          stableCount++;
          console.log(`VAE.AxeIntegration: DOM stable check ${stableCount}/${requiredStableChecks}`, {
            domLength: currentDOMState,
            widgetElements: currentWidgetElementCount
          });
          
          if (stableCount >= requiredStableChecks) {
            console.log('VAE.AxeIntegration: DOM is stable, proceeding with analysis', {
              finalDOMLength: currentDOMState,
              finalWidgetElements: currentWidgetElementCount,
              totalElements: document.querySelectorAll('*').length
            });
            resolve();
            return;
          }
        } else {
          if (stableCount > 0) {
            console.log('VAE.AxeIntegration: DOM changed, resetting stability counter', {
              domLengthChange: currentDOMState - lastDOMState,
              widgetElementChange: currentWidgetElementCount - lastWidgetElementCount
            });
          }
          stableCount = 0; // Reset counter if DOM changed
          lastDOMState = currentDOMState;
          lastWidgetElementCount = currentWidgetElementCount;
        }
        
        setTimeout(checkStability, checkInterval);
      };
      
      // Start stability check
      checkStability();
      
      // Fallback timeout to prevent infinite waiting
      setTimeout(() => {
        console.warn('VAE.AxeIntegration: DOM stability timeout reached, proceeding anyway', {
          finalDOMLength: document.documentElement.outerHTML.length,
          finalWidgetElements: document.querySelectorAll('[data-vae-id]').length,
          totalElements: document.querySelectorAll('*').length
        });
        resolve();
      }, timeout);
    });
    */
  }

  // Run axe-core analysis
  async function runAxeAnalysis(options = {}) {
    try {
      const analysisStartTime = Date.now();
      console.log('VAE.AxeIntegration: Starting axe-core analysis...', {
        timestamp: new Date().toISOString(),
        caller: new Error().stack.split('\n')[2].trim(), // Log who called this function
        initialDOMElements: document.querySelectorAll('*').length,
        initialWidgetElements: document.querySelectorAll('[data-vae-id]').length
      });
      
      if (!axeLoaded) {
        await loadAxeCore();
      }

      // Wait for DOM to be stable before analysis
      await waitForDOMStability();

      // Build exclude list dynamically to ensure widget elements exist
      const excludeSelectors = [
        '[data-vae-widget]',
        '[data-vae-overlay]',
        '.vae-widget',
        '.vae-overlay',
        '#vae-widget-container',
        // Add more specific widget selectors
        '[id^="vae-"]',
        '[class*="vae-"]'
      ];

      // Check which selectors actually exist and log them with counts
      const existingExcludes = [];
      excludeSelectors.forEach(selector => {
        const elements = document.querySelectorAll(selector);
        if (elements.length > 0) {
          console.log(`VAE.AxeIntegration: Found ${elements.length} widget elements to exclude: ${selector}`);
          existingExcludes.push(selector);
        }
      });

      if (existingExcludes.length === 0) {
        console.warn('VAE.AxeIntegration: No widget elements found to exclude. Widget may not be fully initialized.');
      } else {
        console.log(`VAE.AxeIntegration: Total widget selectors to exclude: ${existingExcludes.length}`);
      }

      const defaultOptions = {
        // Focus on WCAG 2.1 AA criteria
        tags: ['wcag2a', 'wcag2aa', 'wcag21aa'],
        // Exclude elements that might interfere with our widget - use only existing selectors
        exclude: existingExcludes.length > 0 ? existingExcludes : excludeSelectors
      };

      const axeOptions = { ...defaultOptions, ...options };
      
      console.log('VAE.AxeIntegration: Running axe with options:', {
        tags: axeOptions.tags,
        excludeCount: axeOptions.exclude.length,
        excludeSelectors: axeOptions.exclude
      });
      
      return new Promise((resolve, reject) => {
        window.axe.run(document, axeOptions, (err, results) => {
          if (err) {
            console.error('VAE.AxeIntegration: axe-core analysis failed:', err);
            reject(err);
          } else {
            console.log(`VAE.AxeIntegration: axe-core found ${results.violations.length} violations`);
            
            // Log detailed analysis information for debugging
            console.log('VAE.AxeIntegration: Analysis details:', {
              timestamp: new Date().toISOString(),
              url: window.location.href,
              domElementCount: document.querySelectorAll('*').length,
              violations: results.violations.length,
              passes: results.passes.length,
              incomplete: results.incomplete.length,
              inapplicable: results.inapplicable.length,
              excludedSelectors: axeOptions.exclude.length
            });
            
            // Additional filtering: Remove any violations that target widget elements
            const filteredResults = filterWidgetViolations(results);
            console.log(`VAE.AxeIntegration: After widget filtering: ${filteredResults.violations.length} violations`);
            
            // Log the difference if any violations were filtered out
            const filteredCount = results.violations.length - filteredResults.violations.length;
            if (filteredCount > 0) {
              console.log(`VAE.AxeIntegration: Filtered out ${filteredCount} widget-related violations`);
            }
            
            resolve(filteredResults);
          }
        });
      });
    } catch (error) {
      console.error('VAE.AxeIntegration: Error during axe analysis:', error);
      throw error;
    }
  }

  // Additional filtering to remove widget-related violations
  function filterWidgetViolations(results) {
    const widgetSelectors = [
      '[data-vae-widget]',
      '[data-vae-overlay]',
      '.vae-widget',
      '.vae-overlay',
      '#vae-widget-container'
    ];

    const filteredViolations = results.violations.map(violation => {
      const filteredNodes = violation.nodes.filter(node => {
        const selector = node.target[0];
        
        // Check if the target element is a widget element
        const isWidgetElement = widgetSelectors.some(widgetSelector => {
          try {
            const element = document.querySelector(selector);
            if (!element) return false;
            
            // Check if element matches widget selector directly
            if (element.matches(widgetSelector)) {
              console.log(`VAE.AxeIntegration: Filtering out widget element: ${selector} (matches ${widgetSelector})`);
              return true;
            }
            
            // Check if element is inside a widget container
            const widgetContainer = element.closest(widgetSelector);
            if (widgetContainer) {
              console.log(`VAE.AxeIntegration: Filtering out element inside widget: ${selector} (inside ${widgetSelector})`);
              return true;
            }
            
            return false;
          } catch (error) {
            console.warn(`VAE.AxeIntegration: Error checking widget selector ${widgetSelector} for ${selector}:`, error);
            return false;
          }
        });
        
        return !isWidgetElement;
      });

      return {
        ...violation,
        nodes: filteredNodes
      };
    }).filter(violation => violation.nodes.length > 0); // Remove violations with no nodes

    return {
      ...results,
      violations: filteredViolations
    };
  }

  // Check if an element is from a Chrome Extension
  function isExtensionElement(selector) {
    try {
      const element = document.querySelector(selector);
      if (!element) return false;
      
      // Check for extension URLs in src/href attributes
      const src = element.getAttribute('src');
      const href = element.getAttribute('href');
      
      if (src && src.startsWith('chrome-extension://')) {
        console.log(`VAE.AxeIntegration: Filtering extension element by src: ${selector}`);
        return true;
      }
      
      if (href && href.startsWith('chrome-extension://')) {
        console.log(`VAE.AxeIntegration: Filtering extension element by href: ${selector}`);
        return true;
      }
      
      // Check for extension-specific attributes
      if (element.hasAttribute('data-ext-id')) {
        console.log(`VAE.AxeIntegration: Filtering extension element by data-ext-id: ${selector}`);
        return true;
      }
      
      // Check for extension-related class names
      const className = element.className || '';
      if (className.includes('hrp') || className.includes('ext-') || className.includes('chrome-')) {
        console.log(`VAE.AxeIntegration: Filtering extension element by class: ${selector}`);
        return true;
      }
      
      // Check for extension-related IDs
      const id = element.id || '';
      if (id.includes('ext-') || id.includes('chrome-') || id.includes('hrp')) {
        console.log(`VAE.AxeIntegration: Filtering extension element by id: ${selector}`);
        return true;
      }
      
      return false;
    } catch (error) {
      console.warn(`VAE.AxeIntegration: Error checking extension element for ${selector}:`, error);
      return false;
    }
  }

  // Transform axe results to our format
  function transformAxeResults(axeResults) {
    const transformedIssues = [];
    let filteredExtensionElements = 0;
    
    console.log('VAE.AxeIntegration: Starting transformation of axe results:', {
      violationsCount: axeResults.violations.length,
      timestamp: new Date().toISOString(),
      caller: new Error().stack.split('\n')[2].trim(),
      domElementsAtTransform: document.querySelectorAll('*').length,
      widgetElementsAtTransform: document.querySelectorAll('[data-vae-id]').length
    });

    axeResults.violations.forEach((violation, violationIndex) => {
      console.log(`VAE.AxeIntegration: Processing violation ${violationIndex + 1}/${axeResults.violations.length}: ${violation.id}`);
      
      violation.nodes.forEach((node, nodeIndex) => {
        try {
          // Skip Chrome Extension elements
          const selector = node.target[0];
          if (isExtensionElement(selector)) {
            console.log(`VAE.AxeIntegration: Skipping Chrome Extension element: ${selector}`);
            filteredExtensionElements++;
            return; // Skip this node
          }

          // Map axe impact to our severity levels
          const severityMap = {
            'critical': 'critical',
            'serious': 'high', 
            'moderate': 'medium',
            'minor': 'low'
          };

          // Extract vaeId from the actual DOM element
          const vaeId = extractVaeId(node.target[0]);

          const issue = {
            // Core identification
            id: `axe-${violation.id}-${node.target[0]}`,
            source: 'axe-core',
            wcagCriterion: extractWcagCriterion(violation.tags),
            
            // Issue details
            description: violation.description,
            help: violation.help,
            helpUrl: violation.helpUrl,
            impact: severityMap[violation.impact] || 'medium',
            
            // Element information
            selector: node.target[0],
            html: node.html,
            vaeId: vaeId, // Include VAE ID for fix application
            
            // Axe-specific data
            axeRuleId: violation.id,
            axeImpact: violation.impact,
            axeTags: violation.tags,
            
            // Fix information
            failureSummary: node.failureSummary,
            any: node.any,
            all: node.all,
            none: node.none,
            
            // Metadata
            timestamp: new Date().toISOString(),
            isAutoFixable: determineAutoFixability(violation.id) // Smart initial assessment
          };

          transformedIssues.push(issue);
        } catch (transformError) {
          console.error(`VAE.AxeIntegration: Error transforming node ${nodeIndex} of violation ${violation.id}:`, transformError);
          // Continue with other nodes instead of failing completely
        }
      });
    });

    console.log(`VAE.AxeIntegration: Transformation complete. Generated ${transformedIssues.length} issues from ${axeResults.violations.length} violations`, {
      transformationSummary: {
        inputViolations: axeResults.violations.length,
        outputIssues: transformedIssues.length,
        expectedIssues: axeResults.violations.reduce((total, violation) => total + violation.nodes.length, 0),
        filteredExtensionElements: filteredExtensionElements,
        timestamp: new Date().toISOString()
      },
      issuesByImpact: transformedIssues.reduce((acc, issue) => {
        acc[issue.impact] = (acc[issue.impact] || 0) + 1;
        return acc;
      }, {}),
      issuesWithVaeId: transformedIssues.filter(issue => issue.vaeId).length,
      issuesWithoutVaeId: transformedIssues.filter(issue => !issue.vaeId).length
    });
    
    // Validate transformation consistency
    const expectedIssueCount = axeResults.violations.reduce((total, violation) => total + violation.nodes.length, 0);
    if (transformedIssues.length !== expectedIssueCount) {
      console.warn(`VAE.AxeIntegration: Transformation inconsistency detected! Expected ${expectedIssueCount} issues, got ${transformedIssues.length}`);
    }

    return transformedIssues;
  }

  // Extract VAE ID from element using selector
  function extractVaeId(selector) {
    try {
      // Find the element using the axe selector
      const element = document.querySelector(selector);
      if (element) {
        // Check if element has data-vae-id
        const vaeId = element.getAttribute('data-vae-id');
        if (vaeId) {
          console.log(`VAE.AxeIntegration: Found vaeId '${vaeId}' for selector '${selector}'`);
          return vaeId;
        } else {
          // If no vaeId exists, try to generate one using VAE.DOM if available
          if (window.VAE?.DOM?.ensureStructureId) {
            VAE.DOM.ensureStructureId(element);
            const newVaeId = element.getAttribute('data-vae-id');
            if (newVaeId) {
              console.log(`VAE.AxeIntegration: Generated new vaeId '${newVaeId}' for selector '${selector}'`);
              return newVaeId;
            }
          }
          console.warn(`VAE.AxeIntegration: No vaeId found for selector '${selector}'`);
          return null;
        }
      } else {
        console.warn(`VAE.AxeIntegration: Element not found for selector '${selector}'`);
        return null;
      }
    } catch (error) {
      console.error(`VAE.AxeIntegration: Error extracting vaeId for selector '${selector}':`, error);
      return null;
    }
  }

  // Extract WCAG criterion from axe tags
  function extractWcagCriterion(tags) {
    // Look for WCAG criterion tags like 'wcag111', 'wcag131', etc.
    const wcagTag = tags.find(tag => tag.match(/^wcag\d+$/));
    if (wcagTag) {
      // Convert 'wcag111' to '1.1.1'
      const numbers = wcagTag.replace('wcag', '');
      if (numbers.length >= 3) {
        return `${numbers[0]}.${numbers[1]}.${numbers.substring(2)}`;
      }
    }
    return 'unknown';
  }

  // Determine if an axe-core rule is likely auto-fixable
  function determineAutoFixability(ruleId) {
    // Rules that are typically auto-fixable
    const autoFixableRules = [
      'image-alt',           // Missing alt attributes
      'label',               // Missing form labels  
      'button-name',         // Missing button text
      'link-name',           // Missing link text
      'input-button-name',   // Missing input button names
      'aria-hidden-body',    // aria-hidden on body
      'html-has-lang',       // Missing html lang attribute
      'html-lang-valid',     // Invalid html lang attribute
      'landmark-one-main',   // Missing main landmark
      'page-has-heading-one', // Missing h1
      'region',              // Content not in landmarks
      'bypass',              // Missing skip links
      'meta-viewport',       // Missing/invalid viewport meta
      'document-title',      // Missing page title
      'duplicate-id',        // Duplicate IDs (can be fixed by changing IDs)
      'form-field-multiple-labels', // Multiple labels for form fields
      'heading-order',       // Incorrect heading hierarchy
      'list',                // Invalid list structure
      'listitem',            // Invalid list item structure
      'definition-list',     // Invalid definition list structure
      'dlitem'               // Invalid definition list item structure
    ];

    // Rules that typically require manual intervention
    const manualFixRules = [
      'color-contrast',      // Requires design decisions
      'focus-order-semantics', // Requires UX decisions
      'keyboard',            // Requires interaction design
      'audio-caption',       // Requires content creation
      'video-caption',       // Requires content creation
      'object-alt',          // Requires content understanding
      'server-side-image-map', // Requires server-side changes
      'meta-refresh',        // Requires architectural changes
      'blink',               // Requires design changes
      'marquee'              // Requires design changes
    ];

    if (autoFixableRules.includes(ruleId)) {
      return true;
    }
    
    if (manualFixRules.includes(ruleId)) {
      return false;
    }

    // Default: Let LLM decide for unknown rules
    return false;
  }

  // Run multiple analyses to check for consistency
  async function runConsistencyCheck(iterations = 3) {
    console.log(`VAE.AxeIntegration: Running consistency check with ${iterations} iterations...`);
    
    const results = [];
    
    for (let i = 0; i < iterations; i++) {
      console.log(`VAE.AxeIntegration: Consistency check iteration ${i + 1}/${iterations}`);
      
      try {
        const axeAnalysis = await runAxeAnalysis();
        const transformedResults = transformAxeResults(axeAnalysis);
        
        results.push({
          iteration: i + 1,
          violationsCount: axeAnalysis.violations.length,
          transformedCount: transformedResults.length,
          timestamp: new Date().toISOString(),
          ruleIds: axeAnalysis.violations.map(v => v.id).sort()
        });
        
        // Small delay between iterations to allow for any DOM changes
        if (i < iterations - 1) {
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      } catch (error) {
        console.error(`VAE.AxeIntegration: Error in consistency check iteration ${i + 1}:`, error);
        results.push({
          iteration: i + 1,
          error: error.message,
          timestamp: new Date().toISOString()
        });
      }
    }
    
    // Analyze consistency
    const violationCounts = results.filter(r => !r.error).map(r => r.violationsCount);
    const transformedCounts = results.filter(r => !r.error).map(r => r.transformedCount);
    
    const isConsistent = violationCounts.every(count => count === violationCounts[0]) &&
                        transformedCounts.every(count => count === transformedCounts[0]);
    
    const consistencyReport = {
      isConsistent,
      iterations: results.length,
      violationCounts,
      transformedCounts,
      averageViolations: violationCounts.length > 0 ? Math.round(violationCounts.reduce((a, b) => a + b, 0) / violationCounts.length) : 0,
      averageTransformed: transformedCounts.length > 0 ? Math.round(transformedCounts.reduce((a, b) => a + b, 0) / transformedCounts.length) : 0,
      results
    };
    
    console.log('VAE.AxeIntegration: Consistency check complete:', consistencyReport);
    
    if (!isConsistent) {
      console.warn('VAE.AxeIntegration: INCONSISTENT RESULTS DETECTED!', {
        violationRange: `${Math.min(...violationCounts)} - ${Math.max(...violationCounts)}`,
        transformedRange: `${Math.min(...transformedCounts)} - ${Math.max(...transformedCounts)}`
      });
    }
    
    return consistencyReport;
  }

  // Public API
  return {
    loadAxeCore,
    runAxeAnalysis,
    transformAxeResults,
    runConsistencyCheck,
    isLoaded: () => axeLoaded
  };
})(); 